import { List, Map } from "immutable";
import { Action, any, async, AsyncState, bool, button, dropdown, Fun, HttpResult, IOWidget, loadingAsyncState, nothing, Option, wait } from '../main';

export interface Builder<globalModel, model, outcome> {
  missingFields:Fun<WizardStateFields<model>, List<string>>
  toWidget : () => WizardWidget<globalModel, model>
  initialState : () => WizardState<model>
  nestedStepsActive:Fun<WizardState<model>, boolean>
}

export interface DecisionCase<globalModel, decision> {
  widget:WizardWidget<globalModel, decision>
  initialState:() => any
}

export type ChooseBuilder<globalModel, isLast, model, outcome> =
  isLast extends true ? Builder<globalModel, model, outcome>
  : FormBuilder<globalModel, model, outcome>

export class DecisionBuilder<globalModel, isLast extends true | false, decision extends { kind:kinds, value:any }, cases, kinds extends string> {
  cases:Map<kinds, DecisionCase<globalModel, decision>>
  missingFields:Fun<WizardStateFields<decision>, List<string>>
  nestedStepsActive:Fun<WizardState<decision>, boolean>

  addCase = <kind extends (Exclude<decision["kind"], cases> & string), caseValue extends decision["value"]>(
    caseName:kind,
    caseWizard:ChooseBuilder<globalModel, isLast, caseValue, never>)
    : DecisionBuilder<globalModel, isLast, decision, cases | kind, kinds> => {
      const caseWidget:WizardWidget<globalModel, caseValue> = caseWizard.toWidget()
      const decisionWidget:WizardWidget<globalModel, decision> = g0 => (d0:WizardState<decision>) => {
        if (d0.value == undefined || ((d0.value as any).kind != caseName))
          return nothing()
        const caseState0 = d0.value.value
        return caseWidget(g0)(caseState0 as any).map(a => (d0:WizardState<decision>) => {
          let caseState1 = a(d0.value.value)
          let d1 = {...d0, value:{...(d0.value as {}), value:caseState1 } }
          d1.fieldErrors = d1.fieldErrors.remove(caseName)
          if (!caseState1.fieldErrors.isEmpty()) {
            d1.fieldErrors = d1.fieldErrors.set(caseName,
              fieldError(caseState1.fieldErrors.some(e => e.kind != "validating"))("There are some errors in the input", "The input is still validating."))
          }
          return d1 as any
        })
      }

      return new DecisionBuilder<globalModel, isLast, decision, cases | kind, kinds>(
        this.cases.set(caseName, {
          widget:decisionWidget,
          initialState:caseWizard.initialState
        }),
        d => {
          const other = this.missingFields(d)
          if (d && d.value && d.kind && d.kind == caseName) {
            return caseWizard.missingFields(d.value.value).concat(other).toList()
          }
          return other
        },
        d => {
          if (d && d.value && d.value.kind && d.value.kind == caseName) {
            return caseWizard.nestedStepsActive(d.value.value)
          }
          return this.nestedStepsActive(d)
        })
    }

  constructor(cases?:Map<kinds, DecisionCase<globalModel, decision>>, missingFields?:Fun<WizardStateFields<decision>, List<string>>, nestedStepsActive?:Fun<WizardState<decision>, boolean>) {
    this.cases = cases || Map()
    this.missingFields = missingFields || (x => List())
    this.nestedStepsActive = nestedStepsActive || (x => false)
  }
}

export type FormItem<globalModel, model> =
  { kind:"field", fieldName:any, editor:WizardWidget<globalModel, model> }
  | { kind:"optional", fieldName:any, editor:WizardWidget<globalModel, model> }
  | { kind:"decision", fieldName:any, editor:WizardWidget<globalModel, model> }

export type FieldOptions<field> = {
  wrapper?:(errors?:FieldError) => Action<JSX.Element>
  validator?:Fun<field,Promise<ValidationOutcome>>
  optional?:true
}

export type DecisionOptions<globalModel, model, field extends string> = {
  selector?:(choices:field[]) => (errors?:FieldError) => IOWidget<field|undefined, field|undefined>
  selectorFunction?:Fun<WizardState<globalModel>,field | undefined>
}

export type OptionalOptions<field> = {
  checkbox?:(errors?:FieldError) => IOWidget<boolean, boolean>
}

export type WizardOptions<globalModel, model> = {
  buttons?:(errors?:FieldError) => (prevDisabled:boolean, nextDisabled:boolean,
    onPrev:() => Action<WizardState<model>>, onNext:() => Action<WizardState<model>>)
    => WizardWidget<globalModel, model>
}

export type ValidationOutcome = "ok" | { kind:"error", error:string }

export type PreventNesting<T> =
  T extends WizardState<infer R> ? never
  : T

export type WizardStateFields<T> =
  T extends Option<infer R> ? WizardState<R>
  : T extends { formType:"steps" } ? { [P in Exclude<keyof T, "formType">]?: WizardState<T[P]> } & { step:Exclude<keyof T, "formType"> | "done" }
  : T extends { kind:infer K, value:infer V } ? { kind:K, value:WizardState<V> }
  : T extends number|string|boolean ? T
  : T extends Object ? { [P in keyof T]?: WizardStateFields<T[P]> }
  : T

export type OptionArgument<t> = t extends Option<infer r> ? r : never

export type FieldError = { kind:"error"|"validating", message:string }
export const fieldError = (isError:boolean) => (errorMessage:string, validationMessage:string) : FieldError =>
  isError ? { kind:"error", message:errorMessage }
  : { kind:"validating", message:validationMessage }

export type FieldValidator = { key:string, validator:AsyncState<ValidationOutcome>, queuedValidator?:() => { key:string, validator:AsyncState<ValidationOutcome> } }
export type WizardState<model> = {
  value:WizardStateFields<model>,
  fieldErrors:Map<string, FieldError>,
  fieldValidators:Map<string, FieldValidator>,
  firstValidationDone?:true
 }

export type WizardWidget<globalModel, model> = Fun<WizardState<globalModel>, IOWidget<WizardState<model>, Action<WizardState<model>>>>

export class FormBuilder<globalModel, model, outcome> implements Builder<globalModel, model, outcome> {
  items:List<FormItem<globalModel, model>>
  missingFields:Fun<WizardStateFields<model>, List<string>>
  initialState = () : WizardState<model> => ({ value:{}, fieldErrors:Map(), fieldValidators:Map() }) as any
  nestedStepsActive: Fun<WizardState<model>, boolean>
  validator:Fun<WizardStateFields<model>,Promise<ValidationOutcome>>

  toWidget = () : WizardWidget<globalModel, model> =>
    g0 => s0 =>
      any<Action<WizardState<model>>>()([
        ...this.items.map(w => w.editor(g0)(s0)).toArray(),
        ...s0.fieldValidators.map((validator, fieldName) =>
            async<ValidationOutcome>(_ =>
              nothing(),
              // string()(`Validated ${validator.key}`).never(),

            {
              key:validator.key,
              loading:_ =>
                nothing()
                // string()(`Validating ${validator.key}`).never()
            })(validator.validator).map<Action<WizardState<model>>>(a => (s0:WizardState<model>) => {
              const newValidator = a(validator.validator)
              const currValidator = s0.fieldValidators.get(fieldName)
              let s1 = {...s0, fieldValidators:s0.fieldValidators.set(fieldName,
                {...currValidator!, validator:newValidator })}
              if (newValidator.kind == "loaded") {
                if (currValidator && currValidator.queuedValidator) {
                  const newValidator:FieldValidator =
                    {...currValidator, ...currValidator.queuedValidator(),
                      queuedValidator:undefined}
                  s1.fieldValidators = s1.fieldValidators.set(fieldName, newValidator)
                } else {
                  s1.fieldValidators = s1.fieldValidators.remove(fieldName)
                  s1.fieldErrors = s1.fieldErrors.remove(fieldName)
                  if (newValidator.value != "ok") {
                    s1.fieldErrors = s1.fieldErrors.set(fieldName, fieldError(true)(newValidator.value.error,""))
                  }
                }
              }
              return s1
            })
          ).valueSeq().toArray()
      ])

  private validateForm : Action<WizardState<model>> = s1 => {
    if (this.validator) {
      s1.fieldErrors = s1.fieldErrors.remove("__form")
      const validation = this.validator(s1.value)
      const oldValidator:FieldValidator|undefined = s1.fieldValidators.get("__form")
      const validatorAsyncCall = loadingAsyncState<ValidationOutcome>(() =>
        validation.then<HttpResult<ValidationOutcome>>(v =>
          ({ kind:"result", value:v, status:200 })))
      let newValidator = !oldValidator || oldValidator.validator.kind == "loaded" ? {
        key:JSON.stringify(s1.value),
        validator:validatorAsyncCall,
        queuedValidator:undefined
      } : {
        ...oldValidator,
        queuedValidator:() => ({ key:JSON.stringify(s1.value).toString(), validator:validatorAsyncCall })
      }
      s1.fieldValidators = s1.fieldValidators.set("__form", newValidator)
      s1.fieldErrors = s1.fieldErrors.set("__form", fieldError(false)("", "Validating"))
    }

    return s1
  }

  addField = <label extends (Exclude<keyof WizardStateFields<model>, outcome> & string)>(fieldName:label,
    editor:IOWidget<Exclude<PreventNesting<WizardStateFields<model>[label]>, undefined>, Exclude<PreventNesting<WizardStateFields<model>[label]>, undefined>>,
    options?:FieldOptions<Exclude<PreventNesting<WizardStateFields<model>[label]>, undefined>>)
    : FormBuilder<globalModel, model, outcome | label> => {
      const innerEditor:(errors?:FieldError) => IOWidget<Exclude<PreventNesting<WizardStateFields<model>[label]>, undefined>, Exclude<PreventNesting<WizardStateFields<model>[label]>, undefined>> =
        errors => x =>
          editor(x).wrapHTML((options && options.wrapper) ? options.wrapper(errors) : h => h)
      const actualEditor:WizardWidget<globalModel, model> = g0 => s0 => {
        return innerEditor(s0.fieldErrors.get(fieldName))((s0.value[fieldName]) as any).map<Action<WizardState<model>>>(field1 => (s0:WizardState<model>) => {
          let s1:any = s0 ? ({...s0, value:{...(s0.value as {})} }) : this.initialState()
          s1.value[fieldName] = field1
          s1.fieldErrors = s1.fieldErrors.remove(fieldName)
          if (options && options.validator) {
            const validation = options.validator(field1)
            const oldValidator:FieldValidator|undefined = s1.fieldValidators.get(fieldName)
            const validatorAsyncCall = loadingAsyncState<ValidationOutcome>(() => validation.then<HttpResult<ValidationOutcome>>(v => ({ kind:"result", value:v, status:200 })))
            let newValidator = !oldValidator || oldValidator.validator.kind == "loaded" ? {
              key:field1.toString(),
              validator:validatorAsyncCall,
              queuedValidator:undefined
            } : {
              ...oldValidator,
              queuedValidator:() => ({ key:field1.toString(), validator:validatorAsyncCall })
            }
            s1.fieldValidators = s1.fieldValidators.set(fieldName, newValidator)
            s1.fieldErrors = s1.fieldErrors.set(fieldName, fieldError(false)("", "Validating"))
          }
          return this.validateForm(s1)
        })
      }

      return new FormBuilder<globalModel, model, outcome | label>(
        this.validator,
        this.items.push({ kind:"field", fieldName, editor:actualEditor }),
        m => {
          const other = this.missingFields(m)
          if (!(options && options.optional) && (m == undefined || m[fieldName] == null || m[fieldName] == undefined)) {
            return other.push(fieldName)
          }
          return other
        },
        m => this.nestedStepsActive(m)
      )
    }

  addOptional = <label extends (Exclude<(keyof WizardStateFields<model>) & (keyof model), outcome> & string)>(fieldName:label,
    option:Builder<globalModel, OptionArgument<model[label]>, never>,
    options?:OptionalOptions<WizardStateFields<model>[label]>)
  : FormBuilder<globalModel, model, outcome | label> => {
    const editor:WizardWidget<globalModel, OptionArgument<model[label]>> = option.toWidget()
    const checkbox:(errors?:FieldError) => IOWidget<boolean, boolean> =
      options && options.checkbox ? options.checkbox : (_ => bool())
    const lifted:WizardWidget<globalModel, model> = g0 => s0 =>
      any<Action<WizardState<model>>>()([
        checkbox(s0.fieldErrors.get(fieldName))(s0.value[fieldName] != undefined).map(selected => (s0:WizardState<model>) => {
          let s1:any = s0 ? ({...s0, value:{...(s0.value as {})} }) : this.initialState()
          s1.value[fieldName] = selected ? { value:{}, fieldErrors:Map(), fieldValidators:Map() } : undefined
          s1.fieldErrors = s1.fieldErrors.remove(fieldName)
          return this.validateForm(s1)
        }),
        s0.value[fieldName] != undefined ?
          editor(g0)((s0.value[fieldName]) as any)
            .map<Action<WizardState<model>>>(a => (s0:WizardState<model>) => {
              let field1 = a(s0.value[fieldName] as any)
              let s1:any = ({...s0, value:{...(s0.value as {})} })
              s1.value[fieldName] = field1
              s1.fieldErrors = s0.fieldErrors.remove(fieldName)
              if (!field1.fieldErrors.isEmpty()) {
                s1.fieldErrors = s1.fieldErrors.set(fieldName,
                  fieldError(field1.fieldErrors.some(e => e.kind != "validating"))(`There are some errors in the input.`, `Validating`)
                )
              }
              return this.validateForm(s1)
            })
        :
          nothing()
      ])

    return new FormBuilder<globalModel, model, outcome | label>(
      this.validator,
      this.items.push({ kind:"optional", fieldName, editor:lifted }),
      m => {
        if (m && m[fieldName] != undefined) {
          return this.missingFields(m)
            .concat(option.missingFields((m[fieldName] as any).value))
            .toList()
        }
        return this.missingFields(m)
      },
      m => this.nestedStepsActive(m)
    )
  }

  addTrailingDecision = <label extends (Exclude<(keyof WizardStateFields<model>) & (keyof model), outcome> & string), kinds extends string>(
    fieldName:label,
    decision:DecisionBuilder<globalModel, true, model[label] & { kind:kinds, value:any }, never, kinds>,
    options?:DecisionOptions<globalModel, model, kinds>)
  : FormBuilder<globalModel, model, (keyof WizardStateFields<model>) & (keyof model)> => {
    return this.addDecision(fieldName, decision as any, options)
  }

  addDecision = <label extends (Exclude<(keyof WizardStateFields<model>) & (keyof model), outcome> & string), kinds extends string>(
    fieldName:label,
    decision:DecisionBuilder<globalModel, false, model[label] & { kind:kinds, value:any }, never, kinds>,
    options?:DecisionOptions<globalModel, model, kinds>)
  : FormBuilder<globalModel, model, outcome | label> => {

    let editor:WizardWidget<globalModel, model> =
      g0 => (s0:WizardState<model>) => {

        let selector:(choices:kinds[]) => (errors?:FieldError) => IOWidget<kinds|undefined, kinds|undefined>
        if (options && options.selectorFunction) {
          const selectorFunction = options.selectorFunction
          const kind1 = selectorFunction(g0)
          if (!s0.value[fieldName] || !(s0.value[fieldName] as any).value || !(s0.value[fieldName] as any).value.kind
              || kind1 != (s0.value[fieldName] as any).fieldErrors.get((s0.value[fieldName] as any).value.kind)) {
            selector = _ => _ => _ => wait<kinds | undefined>(Math.random() * 2)(() => kind1)
          } else {
            selector = _ => _ => _ => nothing()
          }
        } else {
          selector = options && options.selector ? options.selector : (cases => errors => dropdown<kinds>(x => x, cases))
        }

        return any<Action<WizardState<model>>>()([
          selector(decision.cases.keySeq().toArray())(
            s0.value[fieldName] && (s0.value[fieldName] as any).value && (s0.value[fieldName] as any).value.kind &&
            (s0.value[fieldName] as any).fieldErrors.get((s0.value[fieldName] as any).value.kind)
          )(
            s0.value[fieldName] && (s0.value[fieldName] as any).value && (s0.value[fieldName] as any).value.kind
          ).filter(kind1 => !s0.value[fieldName] || !(s0.value[fieldName] as any).value || (s0.value[fieldName] as any).value.kind != kind1)
          .map<Action<WizardState<model>>>(kind1 => (s0:WizardState<model>) => {
            let s1:any = s0 ? {...s0, value:{...(s0.value as {}) }} : this.initialState()
            s1.value[fieldName] = {
              value:{
                kind:kind1,
                value:kind1 ? decision.cases.get(kind1)!.initialState() : undefined
              },
              fieldErrors:Map(),
              fieldValidators:Map()
            }

            if (s1.value[fieldName] && (s1.value[fieldName] as any).value && (s1.value[fieldName] as any).value.kind)
              s1.fieldErrors = s1.fieldErrors.remove(s1.value[fieldName] && (s1.value[fieldName] as any).value && (s1.value[fieldName] as any).value.kind)
            s1.fieldErrors = s1.fieldErrors.remove(fieldName)

            return this.validateForm(s1)
          }),
          s0.value[fieldName] && (s0.value[fieldName] as any).value && (s0.value[fieldName] as any).value.kind ?
            decision.cases.get((s0.value[fieldName] as any).value.kind)!.widget(g0)((s0.value[fieldName] as any))
              .map<Action<WizardState<model>>>(a => (s0:WizardState<model>) => {
                let decisionValue1 = a(s0.value[fieldName] as any)
                let s1:any = {...s0, value:{...(s0.value as {}) }}
                s1.value[fieldName] = decisionValue1

                if (!decisionValue1.fieldErrors.isEmpty())
                  s1.fieldErrors = s1.fieldErrors.set(fieldName,
                    fieldError(decisionValue1.fieldErrors.some(e => e.kind != "validating"))(`There are some errors in the input.`, `Validating`)
                  )
                else
                  s1.fieldErrors = s1.fieldErrors.remove(fieldName)

                return this.validateForm(s1)
              })
          : nothing()
        ])
      }

    return new FormBuilder<globalModel, model, outcome | label>(
      this.validator,
      this.items.push({ kind:"decision", fieldName, editor:editor }),
      m => {
        const others = this.missingFields(m)
        if (m) {
          if (m[fieldName] != undefined) {
            return this.missingFields(m)
              .concat(decision.missingFields((m[fieldName] as any).value))
              .toList()
          } else {
            return this.missingFields(m).push("kind")
          }
        }
        return others
      },
      m => {
        const others = this.nestedStepsActive(m)
        if (m) {
          if (m.value[fieldName] != undefined) {
            return others || decision.nestedStepsActive((m.value[fieldName] as any))
          }
        }
        return others
      },

    )
  }

  constructor(validator?:Fun<WizardStateFields<model>,Promise<ValidationOutcome>>, items?:List<FormItem<globalModel, model>>, missingFields?:Fun<WizardStateFields<model>, List<string>>, nestedStepsActive?:Fun<WizardState<model>, boolean>) {
    this.items = items || List<FormItem<globalModel, model>>()
    this.missingFields = missingFields || (_ => List())
    this.nestedStepsActive = nestedStepsActive || (_ => false)
    this.validator = validator || (_ => new Promise<ValidationOutcome>((res,rej) => res("ok")))
  }
}

export type WizardStep<globalModel, model> = {
  stepName:string,
  editor:WizardWidget<globalModel, model>,
  missingFields:Fun<WizardStateFields<model>, List<string>>
  nestedStepsActive:Fun<WizardState<model>, boolean>
  position:number
  validateStep:Action<WizardState<model>>
}

export class WizardBuilder<globalModel, model extends { formType:"steps" }, outcome> implements Builder<globalModel, model, outcome> {
  wizardOptions?:WizardOptions<globalModel, model>
  steps:Map<string, WizardStep<globalModel, model>>
  initialState:() => WizardState<model>
  nestedStepsActive: Fun<WizardState<model>, boolean> = m => m.value.step != "done"

  missingFields:Fun<WizardStateFields<model>, List<string>> =
    m => {
      const currStep = this.steps.get(m.step)
      let res:List<string> = List()
      if (currStep) {
        res = res.concat(currStep.missingFields(m)).toList()
        if (m.step != "done") {
          const nextSteps = this.steps.valueSeq().filter(s => s.position > currStep.position)
          res = res.concat(nextSteps.map(s => s.stepName)).toList()
        }
      }
      return res
    }

  toWidget = () : WizardWidget<globalModel, model> =>
    g0 => s0 => {
      const validate = (s1:WizardState<model>) =>
        (s1.value as any).step == "done" ? s1
        : this.steps.get((s1.value as any).step)!.validateStep(s1)

      if (!s0.firstValidationDone) {
        s0.firstValidationDone = true
        s0 = validate(s0)
      }

      if (!this.steps.has(s0.value.step) && s0.value.step != "done") {
        console.warn(`Warning: step ${s0.value.step} does not exist, please check initial state and model definition.`)
        return nothing()
      }
      const currStep = this.steps.get(s0.value.step)
      if (!currStep) return nothing()

      const nextStep:WizardStep<globalModel, model>|undefined = this.steps.valueSeq().filter(s => s.position > currStep.position).minBy(s => s.position)
      const prevStep:WizardStep<globalModel, model>|undefined = this.steps.valueSeq().filter(s => s.position < currStep.position).maxBy(s => s.position)

      const fieldErrors:Map<string, FieldError> = !s0 || !s0.fieldErrors ? Map() :
        s0.fieldErrors.merge(
          ((s0.value.step != "done" ?
            s0.value[s0.value.step] as any
          : undefined) ||
          { fieldErrors:Map() }).fieldErrors)

      const errorMessage = fieldErrors.has(`${s0.value.step}__form`) ?
        s0.fieldErrors.get(`${s0.value.step}__form`)!.message : "There are some errors in the input."
      const errors = !fieldErrors.isEmpty() ?
        fieldError(fieldErrors.some(e => e.kind != "validating"))(errorMessage, "Validating.") : undefined
      const missingFields = this.missingFields(s0.value)
      const errorsRecap =
        errors ? errors
        : !missingFields.isEmpty() ? fieldError(true)("Some required fields are not filled in.", "")
        : undefined

      const errorsInCurrStep = !fieldErrors.isEmpty() || !currStep.missingFields(s0.value).isEmpty()

      const prevDisabled = prevStep == undefined
      const nextDisabled = (nextStep == undefined || errorsInCurrStep)
      const onPrev = () => (s1:WizardState<model>) =>({...s1, value:{...(s1.value as {}), step:prevStep!.stepName}}) as any
      const onNext = () => (s1:WizardState<model>) =>
        validate(errorsInCurrStep ?
          ({...s1, fieldErrors:s1.fieldErrors.set((s1.value as any).step, errorsRecap || fieldError(true)("Error.", "")) })
        : ({...s1, fieldErrors:Map(), fieldValidators:Map(), value:{...(s1.value as {}), step:nextStep ? nextStep.stepName : "done"}}) as any)

      const buttons:WizardWidget<globalModel, model> =
        this.wizardOptions && this.wizardOptions.buttons ?
          this.wizardOptions.buttons(
            s0.fieldErrors.get(s0.value.step)
          )(prevDisabled, errorsInCurrStep, onPrev, onNext)
        :
          (g0 => s0 => any<Action<WizardState<model>>>()([
            button<Action<WizardState<model>>>("Prev", { disabled:prevDisabled })(onPrev),
            button<Action<WizardState<model>>>("Next", { })(onNext)
          ]))

      return any<Action<WizardState<model>>>()(
        [
          currStep.editor(g0)(s0).map<Action<WizardState<model>>>(a => s0 => {
            let s1 = a(s0)
            const currStep0 = this.steps.get(s0.value.step)
            const currStep1 = this.steps.get(s1.value.step)
            if (currStep0 && currStep1) {
              if (currStep0.nestedStepsActive(s0) == true &&
                currStep1.nestedStepsActive(s1) == false) {
                  return onNext()(s1)
                }
            }
            return s1
          }),
          !currStep.nestedStepsActive(s0) ? buttons(g0)(s0) : nothing(),
          ...s0.fieldValidators.map((validator, fieldName) =>
            async<ValidationOutcome>(_ =>
              nothing(),
              // string()(`Validated ${validator.key}`).never(),

            {
              key:validator.key,
              loading:_ =>
                nothing()
                // string()(`Validating ${validator.key}`).never()
            })(validator.validator).map<Action<WizardState<model>>>(a => (s0:WizardState<model>) => {
              const newValidator = a(validator.validator)
              const currValidator = s0.fieldValidators.get(fieldName)
              let s1 = {...s0, fieldValidators:s0.fieldValidators.set(fieldName,
                {...currValidator!, validator:newValidator })}
              if (newValidator.kind == "loaded") {
                if (currValidator && currValidator.queuedValidator) {
                  const newValidator:FieldValidator =
                    {...currValidator, ...currValidator.queuedValidator(),
                      queuedValidator:undefined}
                  s1.fieldValidators = s1.fieldValidators.set(fieldName, newValidator)
                } else {
                  s1.fieldValidators = s1.fieldValidators.remove(fieldName)
                  s1.fieldErrors = s1.fieldErrors.remove(fieldName)
                  if (newValidator.value != "ok") {
                    s1.fieldErrors = s1.fieldErrors.set(fieldName, fieldError(true)(newValidator.value.error,""))
                  }
                }
              }
              return s1
            })
          ).valueSeq().toArray()
      ]
      )
    }

  addStep = <step extends (Exclude<(keyof WizardStateFields<model>) & (keyof model), outcome> & string)>(
    stepName:step,
    form:FormBuilder<globalModel, model[step], never>,
    validator?:Fun<WizardStateFields<model>[step],Promise<ValidationOutcome>>) :
    WizardBuilder<globalModel, model, outcome | step> => {
      const validateStep : Action<WizardState<model>> = s1 => {
        if (validator) {
          s1.fieldErrors = s1.fieldErrors.remove(`${stepName}__form`)
          const validation = validator(s1.value[stepName])
          const oldValidator:FieldValidator|undefined = s1.fieldValidators.get(`${stepName}__form`)
          const validatorAsyncCall = loadingAsyncState<ValidationOutcome>(() => validation.then<HttpResult<ValidationOutcome>>(v => ({ kind:"result", value:v, status:200 })))
          let newValidator = !oldValidator || oldValidator.validator.kind == "loaded" ? {
            key:JSON.stringify(s1.value),
            validator:validatorAsyncCall,
            queuedValidator:undefined
          } : {
            ...oldValidator,
            queuedValidator:() => ({ key:JSON.stringify(s1.value).toString(), validator:validatorAsyncCall })
          }
          s1.fieldValidators = s1.fieldValidators.set(`${stepName}__form`, newValidator)
          s1.fieldErrors = s1.fieldErrors.set(`${stepName}__form`, fieldError(false)("", "Validating"))
        }

        return s1
      }

      const f:WizardWidget<globalModel, model[step]> = form.toWidget()

      const editor:WizardWidget<globalModel, model> = g0 => (s0:WizardState<model>) => {
        const f0 = s0.value[stepName] || { value:{ }, fieldErrors:Map(), fieldValidators:Map()  }
        return f(g0)(f0 as any).map<Action<WizardState<model>>>(a => (s0:WizardState<model>) => {
          const f1 = a(s0.value[stepName] as any)
          let s1:any = {...s0, value:{...(s0.value as {}) } }
          s1.value[stepName] = f1
          s1.fieldErrors = s1.fieldErrors.remove(stepName)
          if (!f1.fieldErrors.isEmpty()) {
            s1.fieldErrors = s1.fieldErrors.set(stepName, fieldError(f1.fieldErrors.some(e => e.kind != "validating"))("There are some errors in the input.", "Validating."))
          }

          return validateStep(s1)
        })
      }

      return new WizardBuilder<globalModel, model, outcome | step>(
        this.wizardOptions,
        this.steps.set(stepName, {
          stepName:stepName,
          editor:editor,
          missingFields:m => m.step == "done" ? List() :
            m.step == stepName ? form.missingFields(((m[m.step] || { value:{ }, fieldErrors:Map(), fieldValidators:Map() }) as any).value) : List(),
          nestedStepsActive:m => m.value.step == "done" ? false :
            m.value.step == stepName ? form.nestedStepsActive(m.value[m.value.step] as any) : false,
          position:this.steps.count(),
          validateStep:validateStep
        })
      )
    }

  constructor(wizardOptions?:WizardOptions<globalModel, model>, steps?:Map<string, WizardStep<globalModel, model>>) {
    this.wizardOptions = wizardOptions
    this.steps = steps || Map()
    if (steps && !steps.isEmpty()) {
      const first = steps.valueSeq().minBy(s => s.position)!
      this.initialState = (() => ({ value:{ step:first.stepName }, fieldErrors:Map(), fieldValidators:Map() }) as any)
    } else
      this.initialState = (() => ({ value:{ step:"done" }, fieldErrors:Map(), fieldValidators:Map() }) as any)
  }
}
